/**
 * Pure reducers for text/role-bearing ops on the IR Document.
 *
 * Phase FB.3 of docs/design/frontend-mv-architecture-260524.md: extracted
 * from `ir.ts:apply` so each reducer is independently seam-testable
 * (input + op → output, no editor boot, no DOM).
 *
 * Phase FB.8: signature changed from `(ir, op) -> Document` to
 * `(ir, op) -> ReducerResult` ({ir, dirtyPages}). `dirtyPages` is the
 * 1-based page number of the mutated block (page 0 is the doc-level
 * sentinel — not used in this file; see doc-reducers.ts).
 *
 * Layer: reducers. Allowed imports: store/, actions/, util/. MUST NOT
 * import views/, controllers/, selectors/.
 *
 * Reducers in this file:
 *   - setAltTextReducer       — set-alt-text  (Figure.alt)
 *   - setDecorativeReducer    — set-decorative (Figure.decorative)
 *   - setRoleReducer          — set-role (block kind transition)
 *   - setActualTextReducer    — set-actual-text (Run inline OR block content)
 *
 * Every reducer is pure: input not mutated, output is a fresh Document
 * (wrapped in a ReducerResult).
 *
 * Errors: throws IRError on invalid mutations. Callers catch and surface.
 *
 * @module reducers/text-reducers
 */
import { IRError, ROLES_SET, mutateBlocksById, mutateInlineById, coerceRole, findBlockById, } from "../ir.js";
export function setAltTextReducer(ir, op) {
    let found = false;
    const blocks = mutateBlocksById(ir.blocks, op.structId, (b) => {
        if (b.kind !== "Figure") {
            throw new IRError("WrongBlockKind", `set-alt-text requires a Figure block; got ${b.kind} at ${op.structId}`);
        }
        found = true;
        return { ...b, alt: op.after };
    });
    if (!found)
        throw new IRError("StructIdNotFound", `structId ${op.structId} not found`);
    const next = { ...ir, blocks };
    const block = findBlockById(blocks, op.structId);
    return { ir: next, dirtyPages: new Set(block ? [block.page] : []) };
}
export function setDecorativeReducer(ir, op) {
    let found = false;
    const blocks = mutateBlocksById(ir.blocks, op.structId, (b) => {
        if (b.kind !== "Figure") {
            throw new IRError("WrongBlockKind", `set-decorative requires a Figure block; got ${b.kind} at ${op.structId}`);
        }
        found = true;
        return { ...b, decorative: op.after };
    });
    if (!found)
        throw new IRError("StructIdNotFound", `structId ${op.structId} not found`);
    const next = { ...ir, blocks };
    const block = findBlockById(blocks, op.structId);
    return { ir: next, dirtyPages: new Set(block ? [block.page] : []) };
}
export function setRoleReducer(ir, op) {
    if (!ROLES_SET.has(op.after)) {
        throw new IRError("InvalidRoleTransition", `"${op.after}" is not a valid PDF structure role`);
    }
    let found = false;
    const blocks = mutateBlocksById(ir.blocks, op.structId, (b) => {
        found = true;
        return coerceRole(b, op.after);
    });
    if (!found)
        throw new IRError("StructIdNotFound", `structId ${op.structId} not found`);
    const next = { ...ir, blocks };
    const block = findBlockById(blocks, op.structId);
    return { ir: next, dirtyPages: new Set(block ? [block.page] : []) };
}
export function setActualTextReducer(ir, op) {
    // set-actual-text resolution priority:
    //   1) Run inline (structId on a <span data-struct-id> emitted by render.js)
    //   2) text-content block (Heading/Paragraph/Caption/Note/Span) — replace
    //      the block's inline list with a single Text node carrying op.after.
    //      WHY: buildIRFromStructTree emits text blocks with plain
    //      [{kind:"Text"}] (no Run wrapper), so render.js outputs
    //      <h1 data-struct-id=...>Lathe</h1> with no inner span.
    //      events.js's closestStruct then resolves the BLOCK structId,
    //      not a Run id — without this branch every keystroke in a
    //      heading or paragraph throws WrongInlineKind.
    //   3) Figure block — write to .actualText (the AT-only override).
    const { blocks, found } = mutateInlineById(ir.blocks, op.structId, op.after);
    if (found) {
        const next = { ...ir, blocks };
        // Inline mutation: the Run lives inside a Block; the BLOCK's page
        // is the dirty page. mutateInlineById doesn't expose the parent
        // block, but the parent shares the inline's lineage — walking
        // findBlockById for op.structId returns null (op.structId is the
        // Run's id, not a Block's), so fall back to a scan that finds
        // the block whose inline tree contains the Run.
        const page = _findPageForRunStructId(blocks, op.structId);
        return { ir: next, dirtyPages: new Set(page !== null ? [page] : []) };
    }
    let blockFound = false;
    const newBlocks = mutateBlocksById(ir.blocks, op.structId, (b) => {
        blockFound = true;
        switch (b.kind) {
            case "Heading":
            case "Paragraph":
            case "Caption":
            case "Note":
            case "Span":
                return { ...b, content: [{ kind: "Text", value: op.after }] };
            case "Figure":
                return { ...b, actualText: op.after };
            case "List":
            case "Table":
            case "Formula":
            case "Artifact":
                // WHY: set-actual-text operates on text-content blocks only;
                // container/non-text blocks throw rather than mutating silently.
                throw new IRError("WrongInlineKind", `set-actual-text not supported on block kind ${b.kind} at ${op.structId}`);
        }
    });
    if (!blockFound) {
        throw new IRError("StructIdNotFound", `structId ${op.structId} not found as Run inline or block`);
    }
    const next = { ...ir, blocks: newBlocks };
    const block = findBlockById(newBlocks, op.structId);
    return { ir: next, dirtyPages: new Set(block ? [block.page] : []) };
}
/**
 * Find the page of the Block whose inline subtree contains a Run with the
 * given structId. Returns null if no Run with that structId exists. Used
 * only by setActualTextReducer's Run-inline branch — the inline mutator
 * doesn't expose the parent block, so this is the one-shot fallback to
 * compute the dirty page.
 */
function _findPageForRunStructId(blocks, runStructId) {
    function inlineContains(content) {
        if (!content)
            return false;
        for (const il of content) {
            if (il.kind === "Run" && il.structId === runStructId)
                return true;
            if (il.kind === "Run" && Array.isArray(il.content)
                && inlineContains(il.content)) {
                return true;
            }
        }
        return false;
    }
    for (const b of blocks) {
        if ("content" in b && Array.isArray(b.content) && b.content[0] && typeof b.content[0] === "object"
            && "kind" in b.content[0]
            && inlineContains(b.content)) {
            return b.page;
        }
        if (b.kind === "List") {
            for (const item of b.items) {
                for (const ib of item.content) {
                    if ("content" in ib && Array.isArray(ib.content)
                        && inlineContains(ib.content)) {
                        return ib.page;
                    }
                }
            }
        }
    }
    return null;
}
